import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TreeDeciduous, LogIn, AlertCircle } from "lucide-react";
import { t } from "@/i18n/bg";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const login = useAuthStore((s) => s.login);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || t("loginFailed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1762160607442-6ea14f1b1160?crop=entropy&cs=srgb&fm=jpg&q=85')`,
        }}
      >
        <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]" />
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        }} />
      </div>

      <Card data-testid="login-card" className="relative z-10 w-full max-w-md mx-4 border-0 shadow-2xl bg-white/90 backdrop-blur-xl">
        <CardHeader className="text-center pb-2 pt-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-[#14532d] flex items-center justify-center">
              <TreeDeciduous className="w-7 h-7 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl font-semibold tracking-tight text-[#14532d]">
            {t("appName")}
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            {t("appDescription")}
          </p>
        </CardHeader>
        <CardContent className="px-8 pb-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div data-testid="login-error" className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-md p-3 border border-red-100">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">{t("email")}</Label>
              <Input data-testid="login-email-input" id="email" type="email" placeholder="admin@szdp.bg"
                value={email} onChange={(e) => setEmail(e.target.value)} required className="h-11" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">{t("password")}</Label>
              <Input data-testid="login-password-input" id="password" type="password" placeholder="••••••"
                value={password} onChange={(e) => setPassword(e.target.value)} required className="h-11" />
            </div>
            <Button data-testid="login-submit-button" type="submit" disabled={loading}
              className="w-full h-11 bg-[#14532d] hover:bg-[#166534] text-white font-medium transition-colors">
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t("signingIn")}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <LogIn className="w-4 h-4" />
                  {t("signIn")}
                </span>
              )}
            </Button>
          </form>
          <div className="mt-6 pt-4 border-t border-slate-200">
            <p className="text-[11px] text-center text-slate-400">Developed by evol.bg</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
